import { selectBirthday, selectUser } from './userSlice';
import { selectCurrentLocation } from './locationSlice';
import { selectAvailabilityState } from './availabilitySlice';
import { createSelector } from '@reduxjs/toolkit';
import { selectCartPlans, selectSavedPlans } from './cartSlice';
import { RootState } from 'lib/store';
import { selectDynamicValues } from './dynamicSlice';
import {
  convertToEligibilityCase,
  determineMedicareSupEligibility,
} from 'utils/medicareSupEligibility';
import { selectIsMuted, selectShowClosedCaptions } from './mediaSlice';
import {
  calculateMedicarePartBCosts,
  eligibleConverageTypes,
  getEligibilityStatus,
  getIEPStatus,
  getMedSuppEligibilityDates,
  getMedSuppEnrollmentWindow,
  isMedSuppYearRound,
} from 'lib/eligibilityFormulas';
import { selectCurrentPrescriptions } from './prescriptionSlice';
import {
  MedicareApiPlan,
  MedicareApiPrescription,
  MedicareApiUser,
} from 'types/api';
import {
  flattenRecommendationResults,
  selectRecommendationGapPlans,
  selectRecommendationMaPlans,
  selectRecommendationPdpPlans,
  selectRecommendedType,
  type RecommendationSlice,
} from './recommendationSlice';
import {
  planYearForShoppingPreference,
  SUPPORTED_PLAN_YEARS,
} from 'lib/planYears';
import { dollarFormatter, wholeDollarFormatter } from 'utils/formatters';
import { TaxFilingStatus, UserHealthNeeds } from 'types/user';
import { formatSurveyValues } from 'utils/MedicareAPI';
import { calculateFullAge } from 'utils/dobHelpers';
import { parseYesNoValue } from 'utils/yesNo';
import {
  toMedicareApiGender,
  toSunfireEnrollmentGender,
  toSunfireGender,
} from 'utils/sunfireGender';
import {
  filterPlansByYear,
  isSamePlan,
  planIdentityKey,
} from 'utils/selectedPlans';

/* Utility Functions */

const roundToTens = (num: number): number => {
  return Math.round(num / 10) * 10;
};

/* Reused Selectors - Need to be at the top */

export const selectBirthdate = createSelector(
  [selectBirthday],
  (birthday) => new Date(birthday as string)
);

/* API Selectors */

export const selectRecommendationParams = createSelector(
  [selectUser, selectCurrentLocation],
  (user, location) => {
    return {
      county_fips: location.countyFips,
      gender: toMedicareApiGender(user.gender),
      state: location.state,
      tobacco: user.tobacco,
      user_id: user.id,
      zipcode: location.zipcode,
    };
  }
);

export const selectReadyForRecommendation = createSelector(
  [selectUser, selectCurrentLocation],
  (user, location) => {
    if (!user.id) {
      return false;
    }

    if (!location.zipcode) {
      return false;
    }

    // The specialist + hospital visits are on the same form...
    if (!user.pcp_visits) {
      return false;
    }

    return true;
  }
);

/* Cart Selectors */

export const selectCartEmpty = createSelector(
  [selectCartPlans],
  (cartPlans) => cartPlans.length === 0
);
export const selectCartCount = createSelector(
  [selectCartPlans],
  (cartPlans) => cartPlans.length
);

// Saved Plans Selectors

export const selectSavedPlansCount = createSelector(
  [selectSavedPlans],
  (savedPlans) => savedPlans.length
);

// Combined Selectors
export const selectIsPlanSaved = createSelector(
  [
    selectSavedPlans,
    (state: RootState, plan: Pick<MedicareApiPlan, 'id' | 'planYear'>) => plan,
  ],
  (savedPlans, selectedPlan) =>
    savedPlans.some((plan) => isSamePlan(plan, selectedPlan))
);

export const selectIsPlanInCart = createSelector(
  [
    selectCartPlans,
    (state: RootState, plan: Pick<MedicareApiPlan, 'id' | 'planYear'>) => plan,
  ],
  (cartPlans, selectedPlan) =>
    cartPlans.some((plan) => isSamePlan(plan, selectedPlan))
);

/* Eligibility Slice Selectors */

export const selectMedicareSupEligibilityInformation = createSelector(
  [selectCurrentLocation, selectBirthdate],
  (location, birthdate) => {
    return determineMedicareSupEligibility({
      state: location.state,
      userBirthday: birthdate,
      currentDate: new Date(),
    });
  }
);

export const selectEligibleCoverageTypes = createSelector(
  [selectBirthdate, selectCurrentLocation],
  (birthDate, location) =>
    eligibleConverageTypes(new Date(), birthDate, location.state)
);

/* Media Slice Selectors */

export const selectDisplayClosedCaptions = createSelector(
  [selectIsMuted, selectShowClosedCaptions],
  (isMuted, showClosedCaptions) => {
    return isMuted ? true : showClosedCaptions;
  }
);

/* Prescription Slice Selectors */

const selectMedApiPrescriptions = createSelector(
  [selectCurrentPrescriptions],
  (prescriptions) => {
    return prescriptions.flatMap((prescription) => {
      const ndc = prescription.ndc?.trim() ?? '';
      const quantity = Number(prescription.quantity);
      const period = Number(prescription.frequency);

      // Empty amount fields become 0 via Number(''). The Med API rejects
      // non-positive quantities with 400, which fails the whole user update.
      if (
        !ndc ||
        !Number.isFinite(quantity) ||
        quantity <= 0 ||
        !Number.isFinite(period) ||
        period <= 0
      ) {
        return [];
      }

      return [
        {
          ndc,
          quantity,
          period,
        } as MedicareApiPrescription,
      ];
    });
  }
);

/* Recommendation Slice Selectors */

export type RecommendedPlansForYear = {
  mapd: MedicareApiPlan | null;
  gap: MedicareApiPlan | null;
  pdp: MedicareApiPlan | null;
};

const firstPlan = (
  results: MedicareApiPlan[] | undefined
): MedicareApiPlan | null =>
  results && results.length > 0 ? results[0] : null;

export const recommendationPlansByYear = (
  recommendation: RecommendationSlice | undefined
): Record<number, RecommendedPlansForYear> => {
  if (!recommendation) {
    return {};
  }

  const years = new Set<number>([
    ...Object.keys(recommendation.maPlansByYear ?? {}).map(Number),
    ...Object.keys(recommendation.gapPlansByYear ?? {}).map(Number),
    ...Object.keys(recommendation.pdpPlansByYear ?? {}).map(Number),
  ]);

  const byYear: Record<number, RecommendedPlansForYear> = {};
  for (const year of years) {
    if (!Number.isInteger(year)) {
      continue;
    }

    byYear[year] = {
      mapd: firstPlan(recommendation.maPlansByYear[year]?.results),
      gap: firstPlan(recommendation.gapPlansByYear[year]?.results),
      pdp: firstPlan(recommendation.pdpPlansByYear[year]?.results),
    };
  }

  return byYear;
};

export const plansToCartForType = (
  plansByYear: Record<number, RecommendedPlansForYear>,
  type: 'mapd' | 'gap_pdp'
): MedicareApiPlan[] => {
  const plans: MedicareApiPlan[] = [];

  for (const year of SUPPORTED_PLAN_YEARS) {
    const yearPlans = plansByYear[year];
    if (!yearPlans) {
      continue;
    }

    if (type === 'mapd') {
      if (yearPlans.mapd) {
        plans.push(yearPlans.mapd);
      }
    } else {
      if (yearPlans.gap) {
        plans.push(yearPlans.gap);
      }
      if (yearPlans.pdp) {
        plans.push(yearPlans.pdp);
      }
    }
  }

  return plans;
};

export const selectRecommendationPlansByYear = createSelector(
  [(state: RootState) => state.recommendation],
  recommendationPlansByYear
);

export const selectRecommendation = createSelector(
  [(state: RootState) => state.recommendation],
  (recommendations) => {
    if (!recommendations) {
      return [];
    }

    return [
      ...flattenRecommendationResults(recommendations.maPlansByYear),
      ...flattenRecommendationResults(recommendations.gapPlansByYear),
      ...flattenRecommendationResults(recommendations.pdpPlansByYear),
    ];
  }
);

// Best plan for the selected year, or for an explicit year when one is passed.
// Results are already sorted by score/cost.
export const selectBestGapPlan = (
  state: RootState,
  year?: number
): MedicareApiPlan | null =>
  firstPlan(selectRecommendationGapPlans(state, year).results);

export const selectBestPdpPlan = (
  state: RootState,
  year?: number
): MedicareApiPlan | null =>
  firstPlan(selectRecommendationPdpPlans(state, year).results);

export const selectBestMaPlan = (
  state: RootState,
  year?: number
): MedicareApiPlan | null =>
  firstPlan(selectRecommendationMaPlans(state, year).results);

// Get best HMO plan (filter for HMO network type)
const selectBestHmoPlan = createSelector(
  [(state: RootState) => selectRecommendationMaPlans(state)],
  (maPlans) => {
    if (!maPlans.results || maPlans.results.length === 0) {
      return null;
    }

    const hmoPlans = maPlans.results.filter((result: MedicareApiPlan) => {
      return ['HMO', 'HMO-POS'].includes(result.network);
    });

    return hmoPlans.length > 0 ? hmoPlans[0] : null;
  }
);

// Get best PPO plan (filter for PPO network type)
const selectBestPpoPlan = createSelector(
  [selectRecommendationMaPlans],
  (maPlans) => {
    if (!maPlans.results || maPlans.results.length === 0) {
      return null;
    }

    const ppoPlans = maPlans.results.filter((result: MedicareApiPlan) => {
      return ['Regional PPO', 'PPO'].includes(result.network);
    });

    return ppoPlans.length > 0 ? ppoPlans[0] : null;
  }
);

export const selectHighCostMedSupp = createSelector(
  [selectBestGapPlan, selectBestPdpPlan],
  (gapPlan, pdpPlan) => {
    if (!gapPlan) return undefined;
    const gapCost = gapPlan.annual_cost || 0;
    const pdpCost = pdpPlan?.annual_cost || 0;
    return dollarFormatter.format(gapCost + pdpCost);
  }
);

export const selectLowCostMedSupp = createSelector(
  [selectBestGapPlan, selectBestPdpPlan, selectBestMaPlan],
  (gapPlan, pdpPlan, maPlan) => {
    if (!gapPlan || !maPlan) return undefined;
    const gapPdpCost = (gapPlan.annual_cost || 0) + (pdpPlan?.annual_cost || 0);
    const maCost = maPlan.annual_cost || 0;
    const savings = maCost - gapPdpCost;
    if (savings > 0) {
      // Round to nearest 10
      return dollarFormatter.format(Math.round(savings / 10) * 10);
    }
    return undefined;
  }
);

// Whether we have both a MAPD and a Med Supp plan recommended
export const selectHasMapdAndMedSuppPlans = createSelector(
  [selectBestMaPlan, selectBestGapPlan],
  (ma, gap) => Boolean(ma) && Boolean(gap)
);

// Figure out what plan set is a better deal
export const selectBestPlanOption = createSelector(
  [selectBestMaPlan, selectBestGapPlan, selectBestPdpPlan],
  (ma, gap, pdp) => {
    if (!ma && !gap) {
      return {
        type: 'none',
        plans: [],
        difference: null,
      };
    }

    if (!gap) {
      return {
        type: 'ma',
        plans: [ma],
        difference: null,
      };
    }

    if (!ma) {
      return {
        type: 'gap_pdp',
        plans: [gap, pdp],
        difference: null,
      };
    }

    // Both ma and gap exist
    const drugCost = pdp?.annual_cost || 0;
    const gapCost = gap.annual_cost + drugCost;

    if (ma.annual_cost < gapCost) {
      return {
        type: 'ma',
        plans: [ma],
        difference: roundToTens(gapCost - ma.annual_cost),
      };
    } else {
      return {
        type: 'gap_pdp',
        plans: [gap, pdp],
        difference: roundToTens(ma.annual_cost - gapCost),
      };
    }
  }
);

// TODO: This would need specific plan detail comparison logic
// to calculate the extra cost for coverage for travel/specialized treatment
// For now, returning undefined as placeholder

export const selectCostUnexpectedExpense = createSelector(
  [selectBestGapPlan, selectBestMaPlan],
  (gapPlan, maPlan) => {
    if (!gapPlan || !maPlan) return undefined;
    const difference = (gapPlan.annual_cost || 0) - (maPlan.annual_cost || 0);
    if (difference > 0) {
      // Round to nearest 10
      return dollarFormatter.format(Math.round(difference / 10) * 10);
    }
    return undefined;
  }
);

export const selectLowCostHMO = createSelector(
  [selectBestHmoPlan],
  (hmoPlan) => {
    if (!hmoPlan) return undefined;
    return dollarFormatter.format(hmoPlan.annual_cost);
  }
);

export const selectAmountSavedHMO = createSelector(
  [selectBestHmoPlan, selectBestPpoPlan],
  (hmoPlan, ppoPlan) => {
    if (!hmoPlan || !ppoPlan) return undefined;
    const savings = (ppoPlan.annual_cost || 0) - (hmoPlan.annual_cost || 0);
    if (savings > 0) {
      return wholeDollarFormatter.format(Math.round(savings / 10) * 10);
    }
    return undefined;
  }
);

export const selectHasMedSuppInterest = createSelector(
  [selectDynamicValues],
  (dynamicValues) => {
    return (
      dynamicValues.answeredYesToMedSuppQuestions === 'yes' || // TODO: Remove after B2CMED-453 is deployed
      dynamicValues.msSpecializedTreatment === 'yes' ||
      dynamicValues.msUnexpectedMedical === 'yes' ||
      dynamicValues.msGuaranteedIssue === 'yes' ||
      dynamicValues.snowbird === 'yes'
    );
  }
);

export const selectGuaranteedEnrollment = createSelector(
  [selectHasMedSuppInterest, selectBestHmoPlan, selectBestPpoPlan],
  (hasMedSuppInterest, hmoPlan, ppoPlan) => {
    let isHMOBetter = false;

    if (hmoPlan) {
      if (!ppoPlan || hmoPlan.annual_cost < ppoPlan.annual_cost) {
        isHMOBetter = true;
      }
    }

    return {
      hasMedSuppInterest,
      isHMOBetter,
    };
  }
);

export const selectIsUserLoggedIn = createSelector(
  [selectUser],
  (user) => user.auth0_id !== ''
);

export const selectHasRecommendation = createSelector(
  [selectRecommendedType, selectUser],
  (recommendedType, user) =>
    Boolean(recommendedType || user.recommendation_type)
);

// Recommended plan(s) of the chosen type for every stored plan year.
export const selectAlexRecommendedPlan = createSelector(
  [selectRecommendationPlansByYear, selectRecommendedType],
  (plansByYear, recommendedType) => {
    if (recommendedType !== 'mapd' && recommendedType !== 'gap_pdp') {
      return null;
    }

    const plans = plansToCartForType(plansByYear, recommendedType);
    return plans.length > 0 ? plans : null;
  }
);

export const selectIsPlanRecommended = createSelector(
  [
    selectAlexRecommendedPlan,
    (state: RootState, plan: Pick<MedicareApiPlan, 'id' | 'planYear'>) => plan,
  ],
  (recPlans, selectedPlan) => {
    return recPlans?.some((plan) => isSamePlan(plan, selectedPlan)) ?? false;
  }
);

/* User Slice Selectors */

const selectAge = createSelector([selectBirthdate], (birthdate) => {
  if (!birthdate) {
    return 0;
  }

  return calculateFullAge(birthdate);
});

const selectIncome = createSelector([selectUser], (user) => user.income || 0);

const selectTaxFilingStatus = createSelector(
  [selectUser],
  (user) => user.filing_status || TaxFilingStatus.Single
);

const selectHealthVisits = createSelector([selectUser], (user) => {
  return {
    hospital: user.hospital_visits,
    primaryCare: user.pcp_visits,
    specialist: user.specialist_visits,
  } as UserHealthNeeds;
});

// Most folks will pay 0, we'll handle edge cases later
// Must have worked 10 years and paid taxes for it to be free

export const selectPartBAmount = createSelector(
  [selectIncome, selectTaxFilingStatus],
  (income, filingStatus) => {
    return calculateMedicarePartBCosts(income, filingStatus);
  }
);

export const selectMedApiUserParams = createSelector(
  [
    selectCurrentLocation,
    selectAge,
    selectHealthVisits,
    selectMedApiPrescriptions,
    selectUser,
  ],
  (location, age, visits, prescriptions, user) => {
    const survey = formatSurveyValues(visits);

    return {
      id: user.id,
      age,
      zip_code: location.zipcode,
      state_code: location.state,
      gender: toMedicareApiGender(user.gender),
      // Profile tobacco starts null. A JSON null 400s the Med API user update.
      // Missing tobacco is not a smoker, matching medigap quote params.
      uses_tobacco: user.tobacco === true,
      prescriptions,
      survey,
    } as MedicareApiUser;
  }
);

export const selectMedSuppRequestParams = createSelector(
  [selectUser, selectBirthdate],
  (user, birthdate) => {
    return {
      birthday: birthdate,
      gender: toSunfireGender(user.gender),
      // Profile tobacco starts null. SunFire quotes and the details page
      // treat a missing flag as a nonsmoker; a JSON null is not a boolean.
      smoker: user.tobacco === true,
    };
  }
);

export const selectEnrollmentData = createSelector(
  [selectCurrentLocation, selectUser],
  (location, user) => {
    return {
      location,
      gender: toSunfireEnrollmentGender(user.gender),
      birthday: user.birthdate,
      firstName: user.given_name,
      lastName: user.family_name,
    };
  }
);

export const selectLastButtonClicked = createSelector(
  [selectDynamicValues],
  (dynamicData) => dynamicData.lastButtonClicked
);

export const selectPreferredShoppingYear = createSelector(
  [selectDynamicValues],
  (dynamicData) => dynamicData.preferredShoppingYear
);

export const selectPreferredPlanYear = createSelector(
  [selectPreferredShoppingYear],
  planYearForShoppingPreference
);

export const selectIsMarried = createSelector(
  [selectUser, selectDynamicValues],
  (user, dynamicData) => {
    // The in-session aym answer wins over a stale profile value. Title-case
    // "Yes"/"No" and lowercase "yes"/"no" both appear in CMS / form data.
    const fromSession = parseYesNoValue(dynamicData.currentlyMarried);
    if (fromSession !== null) {
      return fromSession;
    }

    return user.currently_married === true;
  }
);

// Unlike selectIsMarried, this doesn't default to false when marital status
// is unanswered - only a confirmed "not married" answer counts here.
export const selectIsKnownNotMarried = createSelector(
  [selectUser, selectDynamicValues],
  (user, dynamicData) => {
    const fromSession = parseYesNoValue(dynamicData.currentlyMarried);
    if (fromSession !== null) {
      return fromSession === false;
    }

    return user.currently_married === false;
  }
);

/* Pathfinder Selectors */

export const selectPathfinderThirdMilestone = createSelector(
  [
    selectBestMaPlan,
    selectBestGapPlan,
    selectDynamicValues,
    selectBestPlanOption,
    selectEligibleCoverageTypes,
    selectHasMedSuppInterest,
  ],
  (
    mapdPlan,
    medSuppPlan,
    dynamicValues,
    bestOption,
    eligibilityWindow,
    hasMedSuppInterest
  ) => {
    return {
      mapdPlan: mapdPlan,
      medSuppPlan: medSuppPlan,
      eligibilityWindow,
      stayInMedSupp: dynamicValues['cim'] === 'yes',
      yesToMedSupp: hasMedSuppInterest,
      bestOption: bestOption.type,
      snowbird: dynamicValues['snowbird'] === 'yes',
    };
  }
);

export const selectMedSuppEligibility = createSelector(
  [selectBirthdate, selectCurrentLocation],
  (birthdate, location) => {
    const today = new Date();

    const { startDate, endDate } = getMedSuppEligibilityDates(birthdate);
    const enrollmentWindow = getMedSuppEnrollmentWindow(
      today,
      birthdate,
      location.state
    );

    const isYearRound = isMedSuppYearRound(location.state);
    const status = getEligibilityStatus(startDate, endDate);

    return {
      year: today.getUTCFullYear(),
      startDate,
      endDate,
      enrollmentWindow,
      isYearRound,
      status,
    };
  }
);

// States with complicated Med Supp Plans/Rules
const COMPLICATED_MED_SUPP_STATES = ['WI', 'MN', 'MA'];

export const selectIsMedSuppComplicated = createSelector(
  [selectCurrentLocation],
  (location) => COMPLICATED_MED_SUPP_STATES.includes(location.state)
);

export const selectCanSellMedSuppInState = createSelector(
  [selectCurrentLocation, selectAvailabilityState],
  (location, availability) =>
    (
      availability.byYear[availability.selectedPlanYear]
        ?.medSuppEligibleStates ?? []
    ).includes(location.state)
);

/* Comparison Selectors */

const selectComparisonPlansByType = (state: RootState) =>
  state.comparison.plansByType;

export const selectComparisonRemovingIds = (state: RootState) =>
  state.comparison.removingIds;

export const selectComparisonPlans = createSelector(
  [
    selectComparisonPlansByType,
    (_state: RootState, planType: string) => planType,
    (_state: RootState, _planType: string, year: string | number) => year,
  ],
  (plansByType, planType, year) =>
    filterPlansByYear(plansByType[planType] ?? [], Number(year))
);

export const selectIsInComparison = createSelector(
  [
    selectComparisonPlansByType,
    selectComparisonRemovingIds,
    (_state: RootState, plan: Pick<MedicareApiPlan, 'id' | 'planYear'>) => plan,
    (
      _state: RootState,
      _plan: Pick<MedicareApiPlan, 'id' | 'planYear'>,
      planType: string
    ) => planType,
  ],
  (plansByType, removingIds, plan, planType) =>
    (plansByType[planType] ?? []).some((p) => isSamePlan(p, plan)) &&
    !removingIds.includes(planIdentityKey(plan))
);

export const selectComparisonFull = createSelector(
  [
    selectComparisonPlansByType,
    selectComparisonRemovingIds,
    (_state: RootState, planType: string) => planType,
    (_state: RootState, _planType: string, year: string | number) => year,
  ],
  (plansByType, removingIds, planType, year) =>
    filterPlansByYear(plansByType[planType] ?? [], Number(year)).filter(
      (p) => !removingIds.includes(planIdentityKey(p))
    ).length >= 2
);

export const selectSixMonthWindowCheck = createSelector(
  [selectCurrentLocation, selectBirthdate],
  (location, birthdate) => {
    const eligibility = determineMedicareSupEligibility({
      state: location.state,
      userBirthday: birthdate,
    });

    const iepStatus = getIEPStatus(birthdate);

    return {
      inWithinSixMonths: iepStatus === 'duringIEP',
      inState: eligibility.canWeSupport,
      isStateYearRound: eligibility.calculationType === 'year_round',
    };
  }
);

export const selectMedSuppEligibilityRouter = createSelector(
  [selectCurrentLocation, selectBirthdate],
  (location, birthdate) => {
    const input = {
      state: location.state,
      userBirthday: birthdate,
      currentDate: new Date(),
      hasExistingMedSupp: false,
      isInAnotherWindowOfferingPartD: false,
    };
    const result = determineMedicareSupEligibility(input);
    const eligibilityCase = convertToEligibilityCase(result, input);
    return { eligibilityCase };
  }
);
